/**
 * Pool de Web Workers para compressão de imagens.
 *
 * Mantém N workers persistentes. enqueue() adiciona à fila; workers livres
 * pegam jobs em ordem FIFO. Buffer é transferido (zero-copy).
 *
 * Worker count: navigator.hardwareConcurrency (cap em 4 pra não saturar).
 */

const MAX_WORKERS = 4;
const DEFAULT_WORKER_COUNT = 4;

let jobCounter = 0;

/**
 * CompressionQueue — pool de workers com fila FIFO.
 */
export class CompressionQueue {
  constructor(workerCount) {
    const hw = (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || DEFAULT_WORKER_COUNT;
    const count = Math.min(Math.max(1, workerCount ?? hw), MAX_WORKERS);
    /** @type {Array<{worker: Worker, busy: boolean, currentId: string|null}>} */
    this.workers = Array.from({ length: count }, () => this._createWorker());
    /** @type {Array<{id: string, type: string, buffer: ArrayBuffer, resolve: Function, reject: Function}>} */
    this.queue = [];
    this._terminated = false;
  }

  _createWorker() {
    const worker = new Worker(new URL('../workers/compressor.worker.js', import.meta.url), {
      type: 'module',
    });
    const slot = { worker, busy: false, currentId: null };

    worker.onmessage = ({ data }) => {
      const { id, error, buffer, originalSize, compressedSize } = data;
      const job = this._jobsById.get(id);
      if (!job) return; // desconhecido (job já foi limpo)
      this._jobsById.delete(id);
      slot.busy = false;
      slot.currentId = null;
      // buffer foi transferido; o caller agora possui a referência.
      if (error) {
        job.reject(new Error(error));
      } else {
        job.resolve({ buffer, originalSize, compressedSize });
      }
      this._pump();
    };

    worker.onerror = (e) => {
      if (slot.currentId) {
        const job = this._jobsById.get(slot.currentId);
        if (job) {
          this._jobsById.delete(slot.currentId);
          job.reject(new Error(e.message || 'Worker error'));
        }
      }
      slot.busy = false;
      slot.currentId = null;
      this._pump();
    };

    return slot;
  }

  /** Map de jobs em andamento (id → {resolve, reject, ...}). */
  _jobsById = new Map();

  /**
   * Enfileira um job. Retorna Promise que resolve com
   * { buffer: ArrayBuffer, originalSize, compressedSize }.
   * @param {{type: 'image/png'|'image/jpeg'|'image/webp', buffer: ArrayBuffer}} job
   */
  enqueue(job) {
    if (this._terminated) {
      return Promise.reject(new Error('Queue foi terminada'));
    }
    return new Promise((resolve, reject) => {
      const id = `job-${++jobCounter}`;
      this.queue.push({ ...job, id, resolve, reject });
      this._jobsById.set(id, { resolve, reject });
      this._pump();
    });
  }

  _pump() {
    if (this._terminated) return;
    for (const slot of this.workers) {
      if (slot.busy || this.queue.length === 0) continue;
      const job = this.queue.shift();
      slot.busy = true;
      slot.currentId = job.id;
      slot.worker.postMessage({ id: job.id, type: job.type, buffer: job.buffer }, [job.buffer]);
    }
  }

  /** Quantos jobs esperando na fila (excluindo em processamento). */
  get pending() {
    return this.queue.length;
  }

  /** Quantos workers no pool. */
  get poolSize() {
    return this.workers.length;
  }

  /** Quantos jobs em processamento agora. */
  get active() {
    return this.workers.filter((w) => w.busy).length;
  }

  terminate() {
    for (const slot of this.workers) slot.worker.terminate();
    this.workers = [];
    for (const job of this.queue) job.reject(new Error('Queue terminada'));
    this.queue = [];
    this._jobsById.clear();
    this._terminated = true;
  }
}

// Singleton lazy (cria 1x na primeira chamada)
let _instance = null;

export function getQueue() {
  if (!_instance) _instance = new CompressionQueue();
  return _instance;
}

export function resetQueue() {
  if (_instance) {
    _instance.terminate();
    _instance = null;
  }
}
